import React from 'react';

import { css, StyleSheet } from 'aphrodite';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';

import {
  ghostWhite,
  lightIndigo,
  softBlue,
  softBlueLight,
  shadowSoftBlue,
  shadowVeryLight,
  warmGray,
  white,
} from 'lib/css/colors';
import { VIEW_MODES } from 'components/codify_db/constants';

function UPBGMVCard({
  show,
  viewMode,
  selectedManufacturer,
  selectedBrand,
  totalGMV,
  totalGMVLoading,
  packerBrandGMV,
  packerBrandGMVLoading,
  unstructuredGMV,
  unstructuredGMVLoading,
  unbrandedProductGMV,
  unbrandedProductGMVLoading,
}) {
  if (!show) return null;

  const subtitle =
    viewMode === VIEW_MODES.MANUFACTURERS
      ? 'All Branded Products'
      : viewMode === VIEW_MODES.BRANDS
      ? `${selectedManufacturer?.name || 'Manufacturer'}`
      : `${selectedBrand?.name || 'Brand'}`;

  return (
    <div className={css(styles.gmvCard)}>
      <div className={css(styles.gmvHeader)}>
        <div className={css(styles.gmvIconContainer)}>
          <div className={css(styles.gmvIcon)}>💰</div>
        </div>
        <div className={css(styles.gmvTitleContainer)}>
          <h5 className={css(styles.gmvTitle)}>Total GMV</h5>
          <div className={css(styles.gmvSubtitle)}>{subtitle}</div>
        </div>
        <div className={css(styles.gmvValueContainer)}>
          {totalGMVLoading ? (
            <div className={css(styles.gmvLoadingContainer)}>
              <Spinner
                animation="border"
                role="status"
                size="sm"
                className={css(styles.gmvSpinner)}
              />
              <span className={css(styles.gmvLoadingText)}>Loading...</span>
            </div>
          ) : totalGMV ? (
            <div className={css(styles.gmvValue)}>
              ${Number(totalGMV.toFixed(0)).toLocaleString('en-US')}
            </div>
          ) : (
            <div className={css(styles.gmvNoData)}>No data available</div>
          )}
        </div>
      </div>
      {viewMode === VIEW_MODES.MANUFACTURERS && (
        <div className={css(styles.additionalGMVContainer)}>
          <div className={css(styles.gmvItem)}>
            <div className={css(styles.gmvItemLabel)}>Packer Brand GMV:</div>
            {packerBrandGMVLoading ? (
              <Spinner
                animation="border"
                role="status"
                size="sm"
                className={css(styles.gmvItemSpinner)}
              />
            ) : packerBrandGMV ? (
              <div className={css(styles.gmvItemValue)}>
                ${Number(packerBrandGMV.toFixed(0)).toLocaleString('en-US')}
              </div>
            ) : (
              <div className={css(styles.gmvItemNoData)}>-</div>
            )}
          </div>
          <div className={css(styles.gmvItem)}>
            <div className={css(styles.gmvItemLabel)}>Unstructured GMV:</div>
            {unstructuredGMVLoading ? (
              <Spinner
                animation="border"
                role="status"
                size="sm"
                className={css(styles.gmvItemSpinner)}
              />
            ) : unstructuredGMV ? (
              <div className={css(styles.gmvItemValue)}>
                ${Number(unstructuredGMV.toFixed(0)).toLocaleString('en-US')}
              </div>
            ) : (
              <div className={css(styles.gmvItemNoData)}>-</div>
            )}
          </div>
          <div className={css(styles.gmvItem)}>
            <div className={css(styles.gmvItemLabel)}>Unbrand Product GMV:</div>
            {unbrandedProductGMVLoading ? (
              <Spinner
                animation="border"
                role="status"
                size="sm"
                className={css(styles.gmvItemSpinner)}
              />
            ) : unbrandedProductGMV ? (
              <div className={css(styles.gmvItemValue)}>
                ${Number(unbrandedProductGMV.toFixed(0)).toLocaleString('en-US')}
              </div>
            ) : (
              <div className={css(styles.gmvItemNoData)}>-</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  gmvCard: {
    background: `linear-gradient(135deg, ${ghostWhite} 0%, ${lightIndigo} 100%)`,
    borderRadius: '16px',
    border: `1px solid ${softBlueLight}`,
    boxShadow: `
      0 4px 12px ${shadowVeryLight},
      0 2px 8px ${shadowSoftBlue},
      inset 0 1px 0 ${white}
    `,
    padding: '16px 24px',
    minWidth: '400px',
    maxWidth: '600px',
    width: '100%',
    transition: 'all 0.2s ease-in-out',
    ':hover': {
      transform: 'translateY(-1px)',
      boxShadow: `
        0 6px 16px ${shadowVeryLight},
        0 4px 12px ${shadowSoftBlue},
        inset 0 1px 0 ${white}
      `,
    },
  },
  gmvHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',
  },
  gmvIconContainer: {
    marginRight: '12px',
  },
  gmvIcon: {
    width: '36px',
    height: '36px',
    backgroundColor: softBlue,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    boxShadow: `0 2px 6px ${shadowSoftBlue}`,
  },
  gmvTitleContainer: {
    flex: 1,
  },
  gmvTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: softBlue,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  gmvSubtitle: {
    fontSize: '12px',
    color: warmGray,
    fontWeight: '500',
    marginTop: '2px',
    opacity: 0.8,
  },
  gmvValueContainer: {
    textAlign: 'center',
  },
  gmvValue: {
    fontSize: '28px',
    fontWeight: '800',
    color: softBlue,
    letterSpacing: '-0.5px',
    textShadow: `0 1px 2px ${shadowVeryLight}`,
    fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  gmvLoadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  gmvSpinner: {
    color: softBlue,
  },
  gmvLoadingText: {
    fontSize: '14px',
    color: warmGray,
    fontWeight: '500',
  },
  gmvNoData: {
    fontSize: '16px',
    color: warmGray,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  additionalGMVContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: '10px',
    paddingTop: '10px',
    paddingLeft: '12px',
    paddingRight: '12px',
    borderTop: `1px solid ${softBlueLight}`,
    gap: '12px 24px',
  },
  gmvItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: '0 0 calc(50% - 12px)',
    minWidth: '160px',
    boxSizing: 'border-box',
  },
  gmvItemLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: warmGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  gmvItemValue: {
    fontSize: '16px',
    fontWeight: '700',
    color: softBlue,
    letterSpacing: '-0.3px',
  },
  gmvItemSpinner: {
    color: softBlue,
    width: '14px',
    height: '14px',
  },
  gmvItemNoData: {
    fontSize: '14px',
    color: warmGray,
    opacity: 0.6,
  },
});

UPBGMVCard.propTypes = {
  show: PropTypes.bool.isRequired,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)).isRequired,
  selectedManufacturer: PropTypes.shape({
    name: PropTypes.string,
  }),
  selectedBrand: PropTypes.shape({
    name: PropTypes.string,
  }),
  totalGMV: PropTypes.number,
  totalGMVLoading: PropTypes.bool.isRequired,
  packerBrandGMV: PropTypes.number,
  packerBrandGMVLoading: PropTypes.bool.isRequired,
  unstructuredGMV: PropTypes.number,
  unstructuredGMVLoading: PropTypes.bool.isRequired,
  unbrandedProductGMV: PropTypes.number,
  unbrandedProductGMVLoading: PropTypes.bool.isRequired,
};

export default UPBGMVCard;
